import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Drawer,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Avatar,
  IconButton,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Restaurant as RecipeIcon,
  LocalOffer as TagIcon,
  MenuBook as BookIcon,
  Speed as QueueIcon,
  Analytics as AnalyticsIcon,
  Cached as CacheIcon,
  Logout as LogoutIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const DRAWER_WIDTH = 240;

const navItems = [
  { path: '/', label: 'dashboard', icon: DashboardIcon },
  { path: '/users', label: 'users', icon: PeopleIcon },
  { path: '/recipes', label: 'recipes', icon: RecipeIcon },
  { divider: true },
  { path: '/tags', label: 'tags', icon: TagIcon },
  { path: '/books', label: 'books', icon: BookIcon },
  { divider: true },
  { path: '/analytics', label: 'analytics', icon: AnalyticsIcon },
  { path: '/queues', label: 'queues', icon: QueueIcon },
  { path: '/cache', label: 'cache', icon: CacheIcon },
];

const Sidebar = ({ themeMode, toggleTheme }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          backgroundColor: theme.palette.background.sidebar,
          borderRight: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
        },
      }}
    >
      {/* Logo / Brand */}
      <Box
        sx={{
          px: 2.5,
          py: 2.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          cursor: 'pointer',
        }}
        onClick={() => navigate('/')}
      >
        {/* Logo mark - stylized brackets */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.25,
          }}
        >
          <Typography 
            sx={{ 
              color: theme.palette.primary.main,
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: '1.6rem',
              fontWeight: 300,
              lineHeight: 1,
            }}
          >
            {'['}
          </Typography>
          <Typography 
            sx={{ 
              color: theme.palette.text.primary,
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: '1.1rem',
              fontWeight: 600,
              lineHeight: 1,
            }}
          >
            r
          </Typography>
          <Typography 
            sx={{ 
              color: theme.palette.primary.main,
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: '1.6rem',
              fontWeight: 300,
              lineHeight: 1,
            }}
          >
            {']'}
          </Typography>
        </Box>
        <Box>
          <Typography 
            sx={{ 
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: '1rem',
              fontWeight: 500,
              letterSpacing: '-0.02em',
              color: theme.palette.text.primary,
              lineHeight: 1,
            }}
          >
            reciply
          </Typography>
          <Typography 
            sx={{ 
              fontFamily: "'Space Mono', monospace",
              fontSize: '0.55rem',
              color: theme.palette.primary.main,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              mt: 0.35,
              fontWeight: 500,
            }}
          >
            Admin
          </Typography>
        </Box>
      </Box>

      {/* Navigation */}
      <Box sx={{ flex: 1, pt: 1, pb: 2, overflow: 'auto' }}>
        <List disablePadding>
          {navItems.map((item, index) => {
            if (item.divider) {
              return (
                <Divider 
                  key={index} 
                  sx={{ 
                    my: 1.5, 
                    mx: 2.5,
                  }} 
                />
              );
            }
            
            const Icon = item.icon;
            const active = isActive(item.path);
            
            return (
              <ListItem key={item.path} disablePadding sx={{ px: 1.5, mb: 0.25 }}>
                <ListItemButton
                  selected={active}
                  onClick={() => navigate(item.path)}
                  sx={{
                    borderRadius: 1.5,
                    py: 1.25,
                    px: 1.5,
                    minHeight: 44,
                    backgroundColor: active 
                      ? `${theme.palette.primary.main}15` 
                      : 'transparent',
                    borderLeft: active 
                      ? `3px solid ${theme.palette.primary.main}` 
                      : '3px solid transparent',
                    '&:hover': {
                      backgroundColor: active 
                        ? `${theme.palette.primary.main}20` 
                        : isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                    },
                    '&.Mui-selected': {
                      '& .MuiListItemIcon-root': {
                        color: theme.palette.primary.main,
                      },
                      '& .MuiListItemText-primary': {
                        color: theme.palette.primary.main,
                        fontWeight: 500,
                      },
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Icon 
                      sx={{ 
                        fontSize: 20,
                        color: active 
                          ? theme.palette.primary.main
                          : isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.45)',
                      }} 
                    />
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.label}
                    primaryTypographyProps={{
                      fontFamily: "'Space Mono', monospace",
                      fontSize: '0.8rem',
                      letterSpacing: '0.01em',
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>

      {/* User Section */}
      <Box sx={{ p: 2, borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}` }}>
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
          }}
        >
          {/* User avatar */}
          <Avatar
            sx={{
              width: 38,
              height: 38,
              backgroundColor: theme.palette.secondary.main,
              borderRadius: 1.5,
              fontFamily: "'DotGothic16', sans-serif",
              fontSize: '0.9rem',
              color: '#fff',
            }}
          >
            {user?.email?.[0]?.toUpperCase() || 'A'}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: "'Space Mono', monospace",
                fontSize: '0.75rem',
                color: theme.palette.text.primary,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                lineHeight: 1.3,
              }}
            >
              {user?.email?.split('@')[0] || 'admin'}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                mt: 0.25,
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  backgroundColor: theme.palette.success.main,
                  borderRadius: '50%',
                }}
              />
              <Typography
                sx={{
                  fontFamily: "'Space Mono', monospace",
                  fontSize: '0.65rem',
                  color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                  textTransform: 'lowercase',
                }}
              >
                online
              </Typography>
            </Box>
          </Box>
          {/* Action buttons */}
          <Box sx={{ display: 'flex', gap: 0.25 }}>
            <Tooltip title={themeMode === 'dark' ? 'Light mode' : 'Dark mode'}>
              <IconButton 
                size="small" 
                onClick={toggleTheme}
                sx={{ 
                  width: 32, 
                  height: 32,
                  color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.45)',
                }}
              >
                {themeMode === 'dark' ? (
                  <LightModeIcon sx={{ fontSize: 18 }} />
                ) : (
                  <DarkModeIcon sx={{ fontSize: 18 }} />
                )}
              </IconButton>
            </Tooltip>
            <Tooltip title="Sign out">
              <IconButton 
                size="small" 
                onClick={signOut}
                sx={{ 
                  width: 32, 
                  height: 32,
                  color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.45)',
                }}
              >
                <LogoutIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default Sidebar;
